import React from 'react';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import { makeStyles } from '@material-ui/core';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { fromS3Url } from '@scale/shared/utils';
import HorizontalBrightnessBlurSliders from 'react-scale/components/scalers/tasks/textcollection/HorizontalBrightnessBlurSliders';
import { useAttachmentZIndexStyle } from './document';
import { useZoomHook, ZoomHookProps } from './useZoomHook';

interface BlurBrightnessImageProps {
  src: string;
  caption?: string;
  addBrightnessSlider?: boolean;
  addBlurSlider?: boolean;
  addZoomSlider?: boolean;
  size: { width: number; height: number };
}

export default function BlurBrightnessImage(props: BlurBrightnessImageProps) {
  const canvas = React.useRef<HTMLDivElement | null>(null);
  const zoomHookProps: ZoomHookProps = {
    size: { width: props.size.width, height: props.size.height },
    canvas,
  };
  const zoomHook = useZoomHook(zoomHookProps);
  const {
    newWidth,
    newHeight,
    offsetX,
    offsetY,
    zoom,
    onZoomIncrease,
    onZoomDecrease,
    onDoubleClick,
    formatPercent,
    Minus,
    Plus,
    zoomLevels,
  } = zoomHook;

  const styles = useBlurBrightnessImageStyles();
  const zIndexStyle = useAttachmentZIndexStyle(1);

  const [brightness, setBrightness] = React.useState(props.addBrightnessSlider ? 0 : 1);
  const [blur, setBlur] = React.useState(props.addBlurSlider ? 0 : 1);
  const [url, setUrl] = React.useState(props.src);

  const CU_SENSITIVE_CONTENT_BUCKET = 'sensitive-cu-documents';

  React.useEffect(() => {
    const { Bucket: bucket } = fromS3Url(props.src);
    if (bucket === CU_SENSITIVE_CONTENT_BUCKET) {
      const signUrl = async () => {
        apiFetch(null, 'internal/sensitive-content-review/signTrustAndSafetyUrl', {
          method: 'GET',
          query: { url: props.src },
        })
          .then(result => {
            setUrl(result.url);
            return;
          })
          .catch(() => {
            setUrl(props.src);
          });
      };

      signUrl();
    } else {
      setUrl(props.src);
    }
  }, [props.src]);

  return (
    <>
      <div>
        {props.addZoomSlider ? (
          <div ref={canvas} className={styles.viewport}>
            <div
              className={styles.canvas}
              onDoubleClick={onDoubleClick}
              style={{
                width: formatPercent(zoom),
                height: formatPercent(zoom),
                ...zIndexStyle,
              }}
            >
              <div
                className={styles.image}
                style={{
                  position: 'absolute',
                  top: offsetY,
                  left: offsetX,
                  width: newWidth,
                  height: newHeight,
                  backgroundImage: `url(${url})`,
                  filter: `brightness(${brightness * 100}%) blur(${(1 - blur) * 50}px`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  ...zIndexStyle,
                }}
              ></div>
            </div>
            <div style={{ width: props.size.width, height: props.size.height }}></div>
          </div>
        ) : (
          <div
            className={styles.image}
            style={{
              backgroundImage: `url(${url})`,
              filter: `brightness(${brightness * 100}%) blur(${(1 - blur) * 50}px`,
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              ...zIndexStyle,
            }}
          >
            <div style={{ width: props.size.width, height: props.size.height }}></div>
          </div>
        )}
      </div>
      <HorizontalBrightnessBlurSliders
        addBrightnessSlider={props.addBrightnessSlider}
        addBlurSlider={props.addBlurSlider}
        addZoomSlider={props.addZoomSlider}
        setBrightness={setBrightness}
        setBlur={setBlur}
        brightness={brightness}
        blur={blur}
        zoom={zoom}
        onZoomIncrease={onZoomIncrease}
        onZoomDecrease={onZoomDecrease}
        Minus={Minus}
        Plus={Plus}
        zoomLevels={zoomLevels}
        formatPercent={formatPercent}
      />
      {!!props.caption && (
        <div className={styles.topbar}>
          <div className={styles.caption} style={zIndexStyle}>
            {props.caption}
          </div>
        </div>
      )}
    </>
  );
}

const useBlurBrightnessImageStyles = makeStyles({
  image: {
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
  },
  topbar: {
    position: 'absolute',
    left: '40px',
    top: '12px',
    display: 'flex',
    flexDirection: 'row',
  },
  caption: {
    background: '#FFFFFF',
    boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.15)',
    borderRadius: '4px',
    textAlign: 'center',
    paddingLeft: '8px',
    paddingRight: '8px',
    paddingTop: '4px',
    paddingBottom: '4px',
    margin: '4px',
  },
  canvas: {
    position: 'absolute',
    top: 0,
    left: 0,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
  },
  viewport: {
    position: 'relative',
    overflow: 'scroll',
    width: '100%',
    height: '100%',
  },
});
